import React, { useState, useEffect } from 'react'
import { usePermission } from '@fs/zion-permissions'
import { GenericError, Route, withRouter, useParams } from '@fs/zion-router'
import { getAnonSession } from '@fs/zion-session'
import { useUser } from '@fs/zion-user'
import zionDebug from '@fs/zion-debug'
import useInPublicCollection from './authHooks'

// turn on in browser console with: localStorage.debug = 'fs:zion-gedcomx:*'
const debug = zionDebug('zion-gedcomx:recordAuthRoute')

export const AuthRouteWrapper = ({ location, children, LoadingSkeleton, checkDiscoveryRoutes }) => {
  const { userLoading, signedIn } = useUser()
  const [inDiscoveryCenter, discoveryStatus] = usePermission('ThemisPrmDiscoveryCenter', 'CrxCtxPublicAccess')
  const [inAffiliateLibrary, libraryStatus] = usePermission('ThemisPrmAffiliate', 'CrxCtxPublicAccess')
  // example discovery path '/search/collection/:collectionId/discovery/:personId'
  // example discovery url could be https://www.familysearch.org/eurona/1950/search/collection/4464515/discovery/6XV1-K2BM
  const params = useParams()
  const matchParams = checkDiscoveryRoutes ? params : null
  const { inPublicCollection, publicLoading } = useInPublicCollection(location?.pathname, location?.search, matchParams)

  if (signedIn || inDiscoveryCenter || inPublicCollection || inAffiliateLibrary) {
    debug(
      'allowing route:  signed in?',
      signedIn,
      'in discovery center?',
      inDiscoveryCenter,
      'in public collection?',
      inPublicCollection,
      'in affiliate library?',
      inAffiliateLibrary
    )
    return children
  }

  if (userLoading || publicLoading || discoveryStatus === 'loading' || libraryStatus === 'loading') {
    debug(
      'loading: user loading?',
      userLoading,
      'public loading?',
      publicLoading,
      'in discovery center loading?',
      discoveryStatus === 'loading',
      'in affiliate libarary loading?',
      libraryStatus === 'loading'
    )
    return LoadingSkeleton || null
  }

  debug('requiring sign in.')

  window.location.replace(`/auth/familysearch/login?returnUrl=${encodeURIComponent(window.location)}`)

  return LoadingSkeleton || null
}

/**
 * Route wrapper to handle "require login" logic for records and search results
 * Skips require login if the user is in a discovery center
 *
 * @param {Object} Component - react component to route to
 * @param {Object} children - react children
 * @param {Object} [LoadingSkeleton] - react component to display while loading
 * @param {Object} location - router location object
 * @param {Object} [match] - router match object
 * @param {Boolean} [checkDiscoveryRoutes] - if this should check discovery type routes or not
 * @param {Object} rest - any other react props
 * @returns {JSX.Element} Component, if allowed; otherwise, user gets redirected to a login page
 * @constructor
 */
const RecordAuthRoute = withRouter(
  ({ component: Component, children, LoadingSkeleton, location, checkDiscoveryRoutes = false, ...rest }) => {
    const [sessionStarted, setSessionStarted] = useState(false)
    const [error, setError] = useState(false)

    // Force an anonymous session - Platform API requires it
    useEffect(() => {
      let cancelled = false
      try {
        getAnonSession().then((session) => {
          if (!cancelled) {
            debug('got anonymous session', session)
            setSessionStarted(true)
          }
        })
      } catch (e) {
        if (!cancelled) {
          setError(e)
        }
      }
      return () => {
        cancelled = true
      }
    }, [])

    // If we can't get an anonymous session, require sign in
    if (error) {
      debug('Could not get anonymous session. Requiring sign in.')
      window.location.replace(`/auth/familysearch/login?returnUrl=${encodeURIComponent(window.location)}`)
      return <GenericError /> // user shouldn't get here; it's just here for testing purposes
    }

    return (
      sessionStarted && (
        <Route {...rest}>
          {(props) => (
            <AuthRouteWrapper
              location={location}
              LoadingSkeleton={LoadingSkeleton}
              checkDiscoveryRoutes={checkDiscoveryRoutes}
            >
              {children || <Component {...props} />}
            </AuthRouteWrapper>
          )}
        </Route>
      )
    )
  }
)

export default RecordAuthRoute
